import React, { useState } from "react";
import axios from "axios";
import { Alert, Button, Clock, InfoCircle, MessageTextSquare01, Spinner } from "@opendate-app/setlist";

import { useMarketingSettingsContext } from "../MarketingSettingsIndexContainer";
import AccountDetailsSection from "bundles/shared/components/AccountDetailsSection";
import SubscriptionSetup from "bundles/shared/components/SubscriptionSetup";
import PaymentMethods from "bundles/shared/components/PaymentMethods";
import { toast } from "react-toastify";
import { toastError } from "../../../../shared/toastHelper";

const styles = {
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: "12px 0",
    textAlign: "center",
  },
  featureIcon: {
    width: 32,
    height: 32,
    color: "var(--sl-fg-quaternary)",
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  secondary: {
    fontSize: 14,
    color: "var(--sl-text-secondary)",
  },
  tertiary: {
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  toastIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    color: "var(--sl-fg-success)",
  },
  toastTitle: {
    fontWeight: 600,
  },
  muted: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
};

const SmsSetupRequired = () => {
  const { team, venueOwnership, updateVenueOwnership, csrfToken } = useMarketingSettingsContext();
  const [settingUpSms, setSettingUpSms] = useState(false);
  const smsStatus = venueOwnership?.sms_status;
  const { reason, cta } = smsStatus || {};

  const isAccountDetailsCta = cta?.label === "Add Account Details";
  const isSetupSmsCta = cta?.label === "Set Up SMS";
  const isBillingCta = cta?.label === "Enable Billing";
  const isUpdatePaymentCta = cta?.label === "Update Payment Method";
  const isPendingReview = !cta && reason;

  const setupSmsAccount = async () => {
    setSettingUpSms(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    try {
      const { data } = await axios.post(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}/twilio_account`);
      if (data.smsStatus) {
        updateVenueOwnership(venueOwnership.id, { sms_status: data.smsStatus, twilio_account: data.twilioAccount });
        setSettingUpSms(false);
      }
    } catch (error) {
      const message = error.response?.data?.error || "Failed to set up SMS";
      toastError(message);
      setSettingUpSms(false);
    }
  };

  const handleAccountDetailsSave = (updatedTeam) => {
    if (updatedTeam.account_details_complete_for_a2p) {
      setupSmsAccount();
    } else if (updatedTeam.missing_a2p_fields?.length > 0) {
      const fieldList = updatedTeam.missing_a2p_fields.join(", ");
      toast(
        <div style={styles.toast}>
          <InfoCircle style={styles.toastIcon} aria-hidden="true" />
          <div>
            <div style={styles.toastTitle}>Account details saved successfully!</div>
            <div style={styles.muted}>Still need: {fieldList}</div>
          </div>
        </div>,
        {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 8000,
          hideProgressBar: true,
          icon: false,
        }
      );
    }
  };

  const refreshSmsStatus = () => {
    window.location.reload();
  };

  const renderInlineComponent = () => {
    if (isAccountDetailsCta) {
      return (
        <AccountDetailsSection
          teamId={team.id}
          csrfToken={csrfToken}
          onSave={handleAccountDetailsSave}
        />
      );
    }

    if (isSetupSmsCta) {
      return (
        <div style={styles.centered}>
          <MessageTextSquare01 style={styles.featureIcon} aria-hidden="true" />
          <div style={styles.secondary}>
            Your business details will be submitted for carrier verification so you can send SMS messages under your business name. This process typically takes 1–2 weeks.
          </div>
          <Button variant="primary" onClick={setupSmsAccount}>
            Submit for Verification
          </Button>
        </div>
      );
    }

    if (isBillingCta) {
      return (
        <div style={styles.stack}>
          <div style={styles.secondary}>
            Send targeted SMS broadcasts and campaigns directly to your fans.
          </div>
          <div style={styles.tertiary}>
            Pay-as-you-go at <strong>$0.012 per message</strong>. No monthly minimums. Billed monthly or when usage exceeds $900.
          </div>
          <SubscriptionSetup
            teamId={team.id}
            csrfToken={csrfToken}
            subscriptionType="sms"
            buttonLabel="Enable SMS"
            onSuccess={refreshSmsStatus}
          />
        </div>
      );
    }

    if (isUpdatePaymentCta) {
      return (
        <div style={styles.stack}>
          <Alert color="warning" title={reason} />
          <PaymentMethods
            teamId={team.id}
            csrfToken={csrfToken}
            mode="manage"
            showHeader={false}
            onSuccess={refreshSmsStatus}
          />
        </div>
      );
    }

    return null;
  };

  if (settingUpSms) {
    return (
      <div style={styles.centered}>
        <Spinner label="Setting up your SMS profile" />
      </div>
    );
  }

  if (isPendingReview) {
    return (
      <div style={styles.centered}>
        <Clock style={styles.featureIcon} aria-hidden="true" />
        <div style={styles.title}>Verification in Progress</div>
        <div style={styles.tertiary}>
          {reason}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.stack}>
      {!isBillingCta && !isSetupSmsCta && !isUpdatePaymentCta && (
        <div style={styles.tertiary}>
          {reason}
        </div>
      )}
      {renderInlineComponent()}
    </div>
  );
};

export default SmsSetupRequired;
